import { SendSystemMetricsRequest } from "@aibotdev/ct-analytics"
import metricsController from "../api/controllers/metrics"
import { MetricsRequest } from "../models/metrics"
import { METRICS_CONFIG } from "./config"
import { isMetricsEnabled } from "./isMetricsEnabled"
import { LongTaskAggregator, LongTaskData } from "./longTaskAggregator"

export type SendMetricsCallback = (metrics: MetricsRequest[]) => Promise<void>

export class MetricsManager {
    private buffer: MetricsRequest[] = []
    private readonly FLUSH_INTERVAL = METRICS_CONFIG.FLUSH_INTERVAL
    private flushTimer: number | null = null
    private sendMetricsCallback: SendMetricsCallback
    private longTaskAggregator: LongTaskAggregator | null = null

    constructor(sendCallback: SendMetricsCallback) {
        this.sendMetricsCallback = sendCallback
        this.startPeriodicFlush()
        this.longTaskAggregator = new LongTaskAggregator(metrics => {
            this.buffer.push({
                Metrics: metrics
            })
        })
    }

    addMetrics(metrics: MetricsRequest) {
        this.buffer.push(metrics)
    }

    addLongTask(task: LongTaskData) {
        this.longTaskAggregator?.addLongTask(task)
    }

    private async flushBuffer() {
        this.longTaskAggregator?.flush()

        if (this.buffer.length === 0) return

        const metricsToSend = [...this.buffer]
        this.buffer = []

        try {
            await this.sendMetricsCallback(metricsToSend)
        } catch (error) {
            console.error("Failed to send metrics:", error)
        }
    }

    private startPeriodicFlush() {
        if (this.flushTimer) {
            clearInterval(this.flushTimer)
        }

        this.flushTimer = window.setInterval(() => {
            this.flushBuffer()
        }, this.FLUSH_INTERVAL)
    }

    flush() {
        this.flushBuffer()
    }

    destroy() {
        if (this.flushTimer) {
            clearInterval(this.flushTimer)
            this.flushTimer = null
        }

        this.longTaskAggregator?.destroy()
        this.buffer = []
    }
}

let metricsManager: MetricsManager | null = null

export const initializeMetrics = () => {
    if (metricsManager) return

    if (isMetricsEnabled()) {
        metricsManager = new MetricsManager(metricsController.sendMetrics)

        window.addEventListener("beforeunload", () => {
            metricsManager?.flush()
            metricsManager?.destroy()
        })

        console.debug("MetricsManager initialized successfully")
    } else {
        console.debug("MetricsManager disabled by configuration")
    }
}

export const getMetricsManager = (): MetricsManager | null => {
    return metricsManager
}

export const addSystemMetrics = (request: SendSystemMetricsRequest) => {
    const currentMetricsManager = getMetricsManager()
    if (!isMetricsEnabled() || !currentMetricsManager) {
        return Promise.resolve()
    }

    const metricsRequest: MetricsRequest = {
        Metrics: request.metrics
    }

    currentMetricsManager.addMetrics(metricsRequest)

    return Promise.resolve()
}
